import React, { useState } from 'react';
import { Clock, ChevronDown, X, Phone, Calendar, FileText, User, Check, CalendarDays } from 'lucide-react';
import { useIndustry } from '../context/IndustryContext';
import { getIndustryData } from '../data/industryDataService';
import Walkthrough from '../components/Walkthrough';

const metricColorSchemes = {
  orange: 'bg-gradient-to-br from-orange-50 to-yellow-50',
  green: 'bg-gradient-to-br from-green-50 to-emerald-50',
  teal: 'bg-gradient-to-br from-teal-50 to-cyan-50',
  gray: 'bg-gradient-to-br from-gray-100 to-slate-50',
  pink: 'bg-gradient-to-br from-pink-50 to-rose-50',
};

const metricWaveColors = {
  orange: 'text-orange-200',
  green: 'text-green-200',
  teal: 'text-teal-200',
  gray: 'text-gray-200',
  pink: 'text-pink-200',
};

const actionFilterOptions = ['All Actions', 'High Impact', 'Follow-ups', 'Upcoming'];
const taskFilterOptions = ['All Tasks', 'Meetings', 'Due Tasks'];

const walkthroughSteps = [
  { target: '[data-testid="dashboard-header"]', title: 'Welcome to Rep View', description: 'You\'re viewing Forsyt as a Sales Rep. This is where reps see their daily priorities, AI-recommended actions, and deals that need attention, all in one place. Use "Switch to CRO View" in the sidebar to see the leadership perspective.' },
  { target: '[data-testid="focus-metric-blue"]', title: 'High Impact Actions', description: 'Count of urgent, high-priority actions that need your attention right now. These are the ones that directly impact revenue.' },
  { target: '[data-testid="focus-metric-amber"]', title: 'Follow-ups Due', description: 'Number of overdue follow-ups and touches. These are leads or deals where outreach is past due and needs immediate action.' },
  { target: '[data-testid="focus-metric-indigo"]', title: 'Opportunities', description: 'Total active accounts in your pipeline today. Gives you a quick sense of how many deals you\'re juggling.' },
  { target: '[data-testid="focus-metric-violet"]', title: 'CRM Tasks', description: 'Pending CRM updates, fields to fill, stages to move, notes to log. Keeping these clean ensures accurate forecasting.' },
  { target: '[data-testid="focus-metric-green"]', title: 'Calls & Meetings', description: 'Scheduled calls and meetings for today. Shows how much of your day is booked with live interactions.' },
  { target: '[data-testid="focus-tab-ai-prioritised-actions"]', title: 'AI Prioritised Actions', description: 'Actions ranked by deal impact. "START HERE" = highest priority. "NEEDS FOLLOW-UP" = overdue outreach. "UPCOMING" = scheduled next.' },
  { target: '[data-testid="action-card-1"]', title: 'Action Card', description: 'Each card shows the specific action, linked account and ARR, and why it matters. Click "Take Action" for an AI-drafted email, or "Acknowledge" to mark as reviewed.' },
  { target: '[data-testid="focus-tab-crm-hygiene"]', title: 'CRM Hygiene', description: 'Scheduled meetings and CRM tasks that need attention today. Use "Change Date" to reschedule or "Mark Complete" to close out tasks.' },
];

const FocusMetricCard = ({ label, value, color }) => (
  <div className={`relative ${metricColorSchemes[color]} rounded-xl p-4 overflow-hidden min-h-[100px] flex flex-col justify-between`} data-testid={`focus-metric-${color}`}>
    <svg className={`absolute right-0 bottom-0 w-24 h-16 ${metricWaveColors[color]}`} viewBox="0 0 96 64" fill="currentColor">
      <path d="M0 64L8 53.3C16 42.7 32 21.3 48 16C64 10.7 80 21.3 96 26.7V64H0Z" opacity="0.5" />
      <path d="M0 64L8 58.7C16 53.3 32 42.7 48 37.3C64 32 80 32 96 32V64H0Z" opacity="0.3" />
    </svg>
    <p className="text-sm font-medium text-gray-700">{label}</p>
    <p className="text-3xl font-bold text-gray-900 relative z-10">{value}</p>
  </div>
);

const ChangeDateModal = ({ task, onClose, onSave }) => {
  const [newDate, setNewDate] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);

  const handlePush = () => {
    if (!newDate) return;
    setPushing(true);
    setTimeout(() => {
      setPushing(false);
      setPushed(true);
      setTimeout(() => {
        onSave(task.id, newDate);
        onClose();
      }, 1200);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="change-date-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[420px] mx-4" data-testid="change-date-content">
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Change Due Date</h2>
              <p className="text-sm text-gray-500 mt-1">{task.title}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1" data-testid="change-date-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Current Due Date</label>
            <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-600">
              <Clock className="w-4 h-4 text-orange-500" />
              <span>{task.dueTime}</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">New Date</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="new-date-input"
            />
          </div>

          {pushed ? (
            <div className="flex items-center gap-2 justify-center py-3 bg-green-50 rounded-lg" data-testid="pushed-success">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-700">Pushed to CRM</span>
            </div>
          ) : (
            <button
              onClick={handlePush}
              disabled={!newDate || pushing}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="push-to-crm-btn"
            >
              {pushing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Pushing to CRM...
                </>
              ) : (
                <>
                  <CalendarDays className="w-4 h-4" />
                  Update & Push to CRM
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const TakeActionModal = ({ action, actionDetails, onClose, onDone }) => {
  const detail = actionDetails?.[action.id] || {};
  const [emailText, setEmailText] = useState(detail.emailDraft || '');
  const [logToCRM, setLogToCRM] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        onDone(action.id);
        onClose();
      }, 1200);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="take-action-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[580px] max-h-[90vh] overflow-y-auto mx-4" data-testid="modal-content">
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-bold text-gray-900">{action.title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1" data-testid="modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium">{action.account}</span>
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-50 text-green-600 text-sm font-bold">{action.dealValue >= 1000000 ? `$${(action.dealValue / 1000000).toFixed(1)}M` : `$${Math.round(action.dealValue / 1000)}K`}</span>
          </div>
          <div className="bg-purple-50/60 border border-purple-100 rounded-xl p-4 space-y-2" data-testid="ai-insight">
            <p className="text-xs font-bold text-gray-800 tracking-wider uppercase">AI INSIGHT</p>
            <p className="text-sm text-gray-700 leading-relaxed">{detail.aiInsight}</p>
          </div>
          {detail.contact && (
            <div className="space-y-3" data-testid="modal-contact">
              <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">CONTACT</p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${detail.contact.avatarColor} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>{detail.contact.avatar}</div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{detail.contact.name}</p>
                  <p className="text-xs text-gray-500">{detail.contact.title} &middot; {detail.contact.email}</p>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-3" data-testid="email-draft-section">
            <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">EMAIL DRAFT</p>
            <textarea value={emailText} onChange={(e) => setEmailText(e.target.value)} className="w-full min-h-[180px] p-4 text-sm text-gray-700 border border-gray-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent leading-relaxed" data-testid="email-draft-textarea" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="log-to-crm">
            <div onClick={() => setLogToCRM(!logToCRM)} className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${logToCRM ? 'bg-blue-600' : 'bg-white border-2 border-gray-300'}`}>
              {logToCRM && <Check className="w-3.5 h-3.5 text-white" />}
            </div>
            <span className="text-sm font-medium text-gray-700">Log to CRM</span>
          </label>

          {sent ? (
            <div className="flex items-center gap-2 justify-center py-3 bg-green-50 rounded-lg" data-testid="action-sent-success">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-700">Action completed & logged to CRM</span>
            </div>
          ) : (
            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="send-action-btn"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send & Complete Action'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function TodaysFocus() {
  const { selectedIndustry } = useIndustry();
  const industryData = getIndustryData(selectedIndustry);
  const todaysFocusMetrics = industryData.todaysFocus.metrics;
  const todaysActions = industryData.todaysFocus.actions;
  const actionDetails = industryData.todaysFocus.actionDetails;
  const scheduledMeetings = industryData.todaysFocus.scheduledMeetings;
  const tasksDueToday = industryData.todaysFocus.tasksDueToday;

  const [activeTab, setActiveTab] = useState('AI Prioritised Actions');
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionFilter, setActionFilter] = useState('All Actions');
  const [taskFilter, setTaskFilter] = useState('All Tasks');
  const [completedActions, setCompletedActions] = useState({});
  const [acknowledgedActions, setAcknowledgedActions] = useState({});
  const [completedTasks, setCompletedTasks] = useState({});
  const [changeDateTask, setChangeDateTask] = useState(null);
  const [rescheduledTasks, setRescheduledTasks] = useState({});

  const handleActionDone = (actionId) => {
    setCompletedActions(prev => ({ ...prev, [actionId]: true }));
  };

  const handleAcknowledge = (actionId) => {
    setAcknowledgedActions(prev => ({ ...prev, [actionId]: true }));
  };

  const handleMarkComplete = (taskId) => {
    setCompletedTasks(prev => ({ ...prev, [taskId]: true }));
  };

  const handleDateSave = (taskId, newDate) => {
    setRescheduledTasks(prev => ({ ...prev, [taskId]: newDate }));
  };

  // Filter actions
  const categoryMap = { 'High Impact': 'START HERE', 'Follow-ups': 'NEEDS FOLLOW-UP', 'Upcoming': 'UPCOMING' };
  const filteredActions = actionFilter === 'All Actions'
    ? todaysActions
    : todaysActions.filter(a => a.category === categoryMap[actionFilter]);

  const groupedActions = filteredActions.reduce((groups, action) => {
    if (!groups[action.category]) groups[action.category] = [];
    groups[action.category].push(action);
    return groups;
  }, {});

  // Filter tasks
  const filteredMeetings = taskFilter === 'All Tasks' || taskFilter === 'Meetings' ? scheduledMeetings : [];
  const filteredTasks = taskFilter === 'All Tasks' || taskFilter === 'Due Tasks' ? tasksDueToday : [];

  const currentFilter = activeTab === 'AI Prioritised Actions' ? actionFilter : taskFilter;
  const currentFilterOptions = activeTab === 'AI Prioritised Actions' ? actionFilterOptions : taskFilterOptions;
  const setCurrentFilter = activeTab === 'AI Prioritised Actions' ? setActionFilter : setTaskFilter;

  return (
    <div className="space-y-6" data-testid="todays-focus-page">
      <Walkthrough steps={walkthroughSteps} pageKey="todays-focus" />

      <p className="text-sm text-gray-500" data-testid="focus-date">
        Tuesday, 18 March &middot; You can make measurable progress with {todaysActions.length} actions
      </p>

      <div className="grid grid-cols-5 gap-4" data-testid="focus-metrics">
        {todaysFocusMetrics.map((metric) => (
          <FocusMetricCard key={metric.label} {...metric} />
        ))}
      </div>

      {/* Tabs + Filter */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-0">
        <nav className="flex gap-8">
          {['AI Prioritised Actions', 'CRM Hygiene'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab
                  ? 'text-gray-900 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`focus-tab-${tab.toLowerCase().replace(/ /g, '-')}`}
            >
              {tab}
            </button>
          ))}
        </nav>
        <div className="relative pb-3" data-testid="focus-filter">
          <select
            value={currentFilter}
            onChange={(e) => setCurrentFilter(e.target.value)}
            className="appearance-none text-sm text-gray-600 hover:text-gray-900 transition-colors pr-6 bg-transparent cursor-pointer focus:outline-none"
          >
            {currentFilterOptions.map(opt => (
              <option key={opt} value={opt}>Filter: {opt}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 top-1/2 -translate-y-[calc(50%+6px)] w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* AI Prioritised Actions Tab */}
      {activeTab === 'AI Prioritised Actions' && (
        <div className="space-y-8" data-testid="ai-actions-list">
          {Object.keys(groupedActions).length > 0 ? Object.entries(groupedActions).map(([category, actions]) => (
            <div key={category} className="space-y-4">
              <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">{category}</p>
              {actions.map((action) => {
                const isDone = completedActions[action.id];
                const isAcknowledged = acknowledgedActions[action.id];

                return (
                  <div key={action.id} className={`rounded-xl p-6 border-l-4 transition-all duration-300 ${isDone || isAcknowledged ? 'bg-green-50/50 border-green-400 opacity-75' : `${action.bgColor} ${action.borderColor}`}`} data-testid={`action-card-${action.id}`}>
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1 space-y-2">
                        {isDone && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-testid={`done-badge-${action.id}`}>
                            <Check className="w-3 h-3" /> Action Completed
                          </span>
                        )}
                        {isAcknowledged && !isDone && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-testid={`ack-badge-${action.id}`}>
                            <Check className="w-3 h-3" /> Acknowledged
                          </span>
                        )}
                        {!isDone && !isAcknowledged && action.badge && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold tracking-wide text-red-600" data-testid="highest-impact-badge">{action.badge}</span>
                        )}
                        <h3 className={`text-base font-bold ${isDone || isAcknowledged ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{action.title}</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">{action.account} &middot; {action.dealValue >= 1000000 ? `$${(action.dealValue / 1000000).toFixed(1)}M` : `$${Math.round(action.dealValue / 1000)}K`}</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">{action.dealTag}</span>
                        </div>
                        {!isDone && !isAcknowledged && action.urgency && (
                          <div className="flex items-center gap-1.5 text-sm text-orange-600">
                            <Clock className="w-3.5 h-3.5" /><span>{action.urgency}</span>
                          </div>
                        )}
                        <p className="text-sm text-gray-500">{action.description}</p>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {isDone || isAcknowledged ? (
                          <div className="flex items-center gap-1.5 px-5 py-2.5 bg-green-100 text-green-700 text-sm font-medium rounded-lg">
                            <Check className="w-4 h-4" /> Done
                          </div>
                        ) : (
                          <>
                            <button onClick={() => setSelectedAction(action)} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-testid={`take-action-${action.id}`}>Take Action</button>
                            <button onClick={() => handleAcknowledge(action.id)} className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors" data-testid={`acknowledge-${action.id}`}>Acknowledge</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )) : (
            <p className="text-center text-sm text-gray-400 py-8" data-testid="no-actions">No actions match this filter.</p>
          )}
        </div>
      )}

      {/* CRM Hygiene Tab */}
      {activeTab === 'CRM Hygiene' && (
        <div className="space-y-8" data-testid="crm-hygiene-list">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900">CRM Tasks</h2>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200" data-testid="synced-badge">
              <Check className="w-3 h-3" /> Synced
            </span>
          </div>

          {filteredMeetings.length > 0 && (
            <div className="space-y-4">
              <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">SCHEDULED TODAY</p>
              {filteredMeetings.map((meeting) => (
                <div key={meeting.id} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start justify-between" data-testid={`meeting-${meeting.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {meeting.type === 'call' ? <Phone className="w-4 h-4 text-gray-500" /> : <Calendar className="w-4 h-4 text-gray-500" />}
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm font-bold text-gray-900">{meeting.title}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span>{meeting.company}</span><span>&middot;</span><User className="w-3 h-3" /><span>{meeting.contact}</span>
                      </div>
                      <p className="text-sm text-gray-500">{meeting.description}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="text-sm font-bold text-gray-900">{meeting.time}</p>
                    <p className="text-xs text-gray-500">{meeting.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredTasks.length > 0 && (
            <div className="space-y-4">
              <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">TASKS DUE TODAY</p>
              {filteredTasks.map((task) => {
                const isCompleted = completedTasks[task.id];
                const isRescheduled = rescheduledTasks[task.id];

                return (
                  <div key={task.id} className={`rounded-xl p-5 border transition-all duration-300 ${isCompleted ? 'bg-green-50/50 border-green-200' : 'bg-white border-gray-100'}`} data-testid={`due-task-${task.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isCompleted ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {isCompleted ? <Check className="w-5 h-5 text-green-600" /> : <FileText className="w-4 h-4 text-gray-500" />}
                        </div>
                        <div className="space-y-1.5">
                          <h3 className={`text-sm font-bold ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</h3>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <span>{task.company}</span><span>&middot;</span><User className="w-3 h-3" /><span>{task.contact}</span>
                          </div>
                          <p className="text-sm text-gray-500">{task.description}</p>
                          {isCompleted ? (
                            <div className="flex items-center gap-1.5 pt-1">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                <Check className="w-3 h-3" /> Completed & Pushed to CRM
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 pt-1">
                              {isRescheduled ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                                  <CalendarDays className="w-3 h-3" /> Rescheduled to {new Date(isRescheduled).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1 text-orange-500 text-xs font-medium"><Clock className="w-3 h-3" /><span>Due: {task.dueTime}</span></div>
                                  <button onClick={() => setChangeDateTask(task)} className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-testid={`change-date-${task.id}`}>Change Date</button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {isCompleted ? (
                          <div className="flex items-center gap-1.5 px-5 py-2.5 bg-green-100 text-green-700 text-sm font-medium rounded-lg">
                            <Check className="w-4 h-4" /> Done
                          </div>
                        ) : (
                          <>
                            <button onClick={() => handleMarkComplete(task.id)} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-testid={`mark-complete-${task.id}`}>Mark Complete</button>
                            <button className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors" data-testid={`view-in-crm-${task.id}`}>View in CRM</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredMeetings.length === 0 && filteredTasks.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8" data-testid="no-tasks">No tasks match this filter.</p>
          )}
        </div>
      )}

      {selectedAction && <TakeActionModal action={selectedAction} actionDetails={actionDetails} onClose={() => setSelectedAction(null)} onDone={handleActionDone} />}
      {changeDateTask && <ChangeDateModal task={changeDateTask} onClose={() => setChangeDateTask(null)} onSave={handleDateSave} />}
    </div>
  );
}
